import React, { useState, useEffect, useCallback, useRef } from "react";
import { authFetch } from "../lib/api";
import { Send, MessageSquare, Calendar, ChevronDown } from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import useAuth from "../hooks/useAuth";
import { Icons } from "../components/icons";

// A proxy error page or an HTML 404 is not JSON. Parsing it threw, and the
// catch blocks reported every such failure as "Network error.", hiding the
// real cause. Fall back to a message that carries the HTTP status instead.
const readJson = async (res) => {
  try {
    return await res.json();
  } catch {
    return {
      success: false,
      message: `Unexpected response from the server (HTTP ${res.status}).`,
    };
  }
};

const describeFetchError = (err) =>
  err?.name === "TimeoutError" || err?.name === "AbortError"
    ? "The server took too long to respond. Please try again."
    : "Unable to reach the server. Check your connection and try again.";

// ─── Helpers ───────────────────────────────────────────────────────────────
const getInitials = (name = "") =>
  name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

const getMonday = (d = new Date()) => {
  const date = new Date(d);
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  date.setHours(0, 0, 0, 0);
  return date;
};

const toDateKey = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const formatHeaderDate = (dateStr) => {
  const d = new Date(dateStr);
  const day = String(d.getDate()).padStart(2, "0");
  const month = d.toLocaleDateString("en-US", { month: "short" });
  const week = d.toLocaleDateString("en-US", { weekday: "short" });
  return { day: `${day} ${month}`, weekday: week };
};

// ─── Comment Modal ─────────────────────────────────────────────────────────
const CommentModal = ({
  taskId,
  taskName,
  commentCount: initCount,
  isSubmitted,
  onClose,
}) => {
  const { user: currentUser } = useAuth();
  const [comments, setComments] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [, setCount] = useState(initCount);
  const [activeMenu, setActiveMenu] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState("");
  const bottomRef = useRef(null);

  const fetchComments = useCallback(async () => {
    try {
      const res = await authFetch(`/api/tasks/${taskId}/comments`);
      const json = await readJson(res);
      if (json?.data) {
        setComments(json.data);
        setCount(json.commentCount);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [comments]);

  const handlePost = async () => {
    if (!input.trim() || posting || isSubmitted) return;
    setPosting(true);
    try {
      const res = await authFetch(`/api/tasks/${taskId}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: input.trim() }),
      });
      const json = await readJson(res);
      if (res.ok && json?.data) {
        setComments((prev) => [...prev, json.data]);
        setCount(json.commentCount);
        setInput("");
      }
    } catch {
    } finally {
      setPosting(false);
    }
  };

  const handleEditClick = (comment) => {
    setEditingId(comment._id);
    setEditingText(comment.text);
    setActiveMenu(null);
  };

  const handleSaveEdit = async (commentId) => {
    if (!editingText.trim()) return;
    try {
      const res = await authFetch(
        `/api/tasks/${taskId}/comments/${commentId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text: editingText.trim() }),
        },
      );
      const json = await readJson(res);
      if (res.ok && json?.data) {
        setComments((prev) =>
          prev.map((c) => (c._id === commentId ? json.data : c)),
        );
      }
    } catch {
    } finally {
      setEditingId(null);
      setEditingText("");
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white w-[95vw] max-w-[500px] rounded-xl relative max-h-[90vh] flex flex-col overflow-hidden shadow-xl">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10"
        >
          ✕
        </button>
        <div className="p-6 border-b">
          <h2 className="text-base font-bold text-gray-800 border-l-4 border-blue-500 pl-3">
            Comments
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Task: <span className="font-medium text-gray-700">{taskName}</span>
          </p>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4 min-h-[180px]">
          {loading ? (
            <p className="text-sm text-gray-400 text-center py-8">Loading...</p>
          ) : comments.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">
              No comments yet. Be the first to comment!
            </p>
          ) : (
            comments.map((c, i) => {
              const isOwn =
                currentUser?._id &&
                (c.author?._id || c.author) === currentUser._id;
              return (
                <div key={c._id || i} className="flex gap-3">
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                    {getInitials(c.author?.name || "You")}
                  </div>
                  <div className="flex-1 min-w-0 bg-gray-50 p-3 rounded-lg">
                    <div className="flex justify-between items-start">
                      <span className="font-semibold text-sm text-gray-800">
                        {c.author?.name || "You"}
                      </span>
                      <span className="text-xs text-gray-400">
                        {c.createdAt
                          ? new Date(c.createdAt).toLocaleString()
                          : ""}
                      </span>
                    </div>
                    {editingId === c._id ? (
                      <div className="mt-2">
                        <textarea
                          value={editingText}
                          onChange={(e) => setEditingText(e.target.value)}
                          className="w-full border rounded-md p-2 text-sm resize-none"
                          rows={3}
                        />
                        <div className="flex justify-end gap-2 mt-2">
                          <button
                            className="px-3 py-1 border rounded-md text-sm"
                            onClick={() => {
                              setEditingId(null);
                              setEditingText("");
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            className="px-3 py-1 bg-blue-500 text-white rounded-md text-sm"
                            onClick={() => handleSaveEdit(c._id)}
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-700 mt-1">{c.text}</p>
                    )}
                  </div>
                  {isOwn && !isSubmitted && editingId !== c._id && (
                    <div className="relative flex-shrink-0">
                      <button
                        className="text-gray-500 px-2"
                        onClick={() =>
                          setActiveMenu(activeMenu === c._id ? null : c._id)
                        }
                      >
                        ⋯
                      </button>
                      {activeMenu === c._id && (
                        <>
                          <div
                            className="fixed inset-0 z-40"
                            onClick={() => setActiveMenu(null)}
                          />
                          <div className="absolute right-0 top-6 bg-white border rounded-md shadow-lg z-50 w-[100px]">
                            <button
                              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                              onClick={() => handleEditClick(c)}
                            >
                              Edit
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
          <div ref={bottomRef} />
        </div>
        {!isSubmitted && (
          <div className="border-t p-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                Y
              </div>
              <div className="flex-1 flex items-center border border-gray-200 rounded-lg focus-within:border-blue-400 overflow-hidden">
                <input
                  type="text"
                  className="flex-1 outline-none text-sm px-3 py-2 bg-transparent"
                  placeholder="Write your comment..."
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handlePost();
                    }
                  }}
                />
              </div>
              <button
                onClick={handlePost}
                disabled={posting || !input.trim()}
                className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition disabled:opacity-40"
              >
                <Send size={18} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main Timesheet Component ───────────────────────────────────────────────
const Timesheet = () => {
  const [timesheetData, setTimesheetData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [editingCell, setEditingCell] = useState(null); // "entryId-dateKey"
  const [editValue, setEditValue] = useState("");
  const [cellError, setCellError] = useState({}); // { "entryId-dateKey": "msg" }
  const [commentModal, setCommentModal] = useState(null); // { taskId, taskName, commentCount }
  // The week is driven by a single "anchor" date — any date the user picks
  // snaps to that date's Monday, which is what actually gets sent to the
  // API. Defaults to the current week.
  const [anchorDate, setAnchorDate] = useState(() => new Date());
  const [showRangePicker, setShowRangePicker] = useState(false);
  const focusEditInput = useCallback((node) => {
    if (node) node.focus({ preventScroll: true });
  }, []);

  const monday = getMonday(anchorDate);
  const weekEnd = new Date(monday);
  weekEnd.setDate(monday.getDate() + 6);
  const startDateStr = toDateKey(monday);

  // Hours may only be logged for the week currently in progress. A future
  // week has not happened yet, so its cells are read-only and the Submit
  // button is hidden — the backend rejects these too.
  const isFutureWeek = monday.getTime() > getMonday(new Date()).getTime();

  // A project's days before its start date are not loggable — the project had
  // not begun. Compared at day granularity so the start date itself is open.
  // A project with no start date recorded never blocks anything.
  const isBeforeProjectStart = (dateKeyStr, projectStartDate) => {
    if (!projectStartDate || !dateKeyStr) return false;
    const start = new Date(projectStartDate);
    if (Number.isNaN(start.getTime())) return false;
    start.setHours(0, 0, 0, 0);
    const target = new Date(`${dateKeyStr}T00:00:00`);
    if (Number.isNaN(target.getTime())) return false;
    return target.getTime() < start.getTime();
  };

  // Earliest start date across every project this user can log against.
  // Weeks entirely before it have nothing to record, so the picker is bounded
  // there and Submit is blocked — otherwise an empty timesheet could be
  // submitted for a week when no project existed at all.
  const [earliestProjectStart, setEarliestProjectStart] = useState(null);

  useEffect(() => {
    const loadEarliestStart = async () => {
      try {
        const res = await authFetch("/api/timesheets/projects-tasks");
        const json = await readJson(res);
        const starts = (json?.data || [])
          .map((g) => g.project?.startDate)
          .filter(Boolean)
          .map((s) => new Date(s))
          .filter((d) => !Number.isNaN(d.getTime()));
        if (starts.length === 0) {
          setEarliestProjectStart(null);
          return;
        }
        const min = new Date(Math.min(...starts.map((d) => d.getTime())));
        min.setHours(0, 0, 0, 0);
        setEarliestProjectStart(min);
      } catch {
        // Leave it null — the picker stays unbounded rather than locking the
        // user out of their own timesheet because one request failed.
        setEarliestProjectStart(null);
      }
    };
    loadEarliestStart();
  }, []);

  // The Monday of the week containing the earliest project start. Selecting
  // any day in that week is allowed; earlier weeks are not.
  const minSelectableDate = earliestProjectStart
    ? getMonday(earliestProjectStart)
    : null;

  const isBeforeAnyProject =
    minSelectableDate !== null && monday.getTime() < minSelectableDate.getTime();

  const formatRangeLabel = (start, end) => {
    const opts = { day: "2-digit", month: "short" };
    const startLabel = start.toLocaleDateString("en-US", opts);
    const endLabel = end.toLocaleDateString("en-US", {
      ...opts,
      year: "numeric",
    });
    return `${startLabel} – ${endLabel}`;
  };

  // ── Fetch timesheet ──────────────────────────────────────────────────────
  const fetchTimesheet = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await authFetch(`/api/timesheets?startDate=${startDateStr}`);
      const json = await readJson(res);
      if (json?.data) setTimesheetData(json.data);
      else setError(json?.message || "Failed to load timesheet.");
    } catch (err) {
      setError(describeFetchError(err));
    } finally {
      setLoading(false);
    }
  }, [startDateStr]);

  useEffect(() => {
    fetchTimesheet();
  }, [fetchTimesheet]);

  // ── Auto-submit scheduler (frontend fallback — backend cron is primary) ──
  useEffect(() => {
    if (!timesheetData || timesheetData.status === "submitted") return;
    // Never schedule an auto-submit for a week that has not started.
    if (isFutureWeek) return;
    const now = new Date();
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 0, 0);
    const msUntilSunday = sunday.getTime() - now.getTime();
    if (msUntilSunday <= 0) return;
    const timer = setTimeout(() => {
      handleSubmit(true);
    }, msUntilSunday);
    return () => clearTimeout(timer);
  }, [timesheetData, monday]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Save hours cell ──────────────────────────────────────────────────────
  const handleCellBlur = async (
    entryId,
    dateKey,
    value,
    cellKey,
    pIdx,
    eIdx,
  ) => {
    setEditingCell(null);
    // Belt-and-braces: the cells are already read-only for locked weeks and
    // for days before the project started.
    if (isLocked) return;
    const projectStartDate =
      timesheetData?.projects?.[pIdx]?.project?.startDate || null;
    if (isBeforeProjectStart(dateKey, projectStartDate)) return;
    const hours = parseFloat(value);
    if (isNaN(hours) || hours < 0) return;

    // Daily total validation — sum hours of all OTHER cells for this day,
    // excluding the exact cell being edited (identified by project+entry index)
    const otherEntriesTotal = (timesheetData?.projects || []).reduce(
      (sum, p, pi) =>
        sum +
        (p.entries || []).reduce((eSum, e, ei) => {
          if (pi === pIdx && ei === eIdx) return eSum;
          const log = (e.logs || []).find((l) => l.dateKey === dateKey);
          return eSum + (log && !log.isLeave ? Number(log.hours) || 0 : 0);
        }, 0),
      0,
    );

    if (otherEntriesTotal + hours > 9) {
      setCellError((prev) => ({ ...prev, [cellKey]: `Maximum 9h Per Day` }));
      setTimeout(
        () =>
          setCellError((prev) => {
            const n = { ...prev };
            delete n[cellKey];
            return n;
          }),
        3000,
      );
      return;
    }

    setSaving(true);
    try {
      let res, json;
      if (timesheetData._id) {
        res = await authFetch(
          `/api/timesheets/${timesheetData._id}/entries/${entryId}/logs`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              date: dateKey,
              hours,
            }),
          },
        );
        json = await readJson(res);
      } else {
        // No timesheet yet — create it. project/task can arrive populated
        // (objects) or as plain ids depending on the scaffold response.
        const allEntries = (timesheetData.projects || []).flatMap((p, pi) =>
          (p.entries || []).map((e, ei) => ({
            project: e.project?._id || e.project,
            task: e.task?._id || e.task,
            logs: (e.logs || []).map((l) => ({
              date: l.dateKey || toDateKey(l.date),
              hours:
                (l.dateKey || toDateKey(l.date)) === dateKey &&
                pi === pIdx &&
                ei === eIdx
                  ? hours
                  : Number(l.hours) || 0,
              isLeave: l.isLeave || false,
            })),
          })),
        );
        res = await authFetch("/api/timesheets", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            startDate: startDateStr,
            endDate: toDateKey(new Date(monday.getTime() + 6 * 86400000)),
            entries: allEntries,
          }),
        });
        json = await readJson(res);
      }

      if (res.ok && json?.data) {
        setTimesheetData(json.data);
        setCellError((prev) => {
          const n = { ...prev };
          delete n[cellKey];
          return n;
        });
      } else {
        setCellError((prev) => ({
          ...prev,
          [cellKey]: json?.message || "Error saving",
        }));
        setTimeout(
          () =>
            setCellError((prev) => {
              const n = { ...prev };
              delete n[cellKey];
              return n;
            }),
          3000,
        );
      }
    } catch (err) {
      setCellError((prev) => ({ ...prev, [cellKey]: describeFetchError(err) }));
    } finally {
      setSaving(false);
    }
  };

  const handleOpenComments = (entry) => {
    if (!entry?.task?._id) return;
    setCommentModal({
      taskId: entry.task._id,
      taskName: entry.task?.name,
      commentCount: entry.commentCount || 0,
    });
  };

  // ── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async (isAuto = false) => {
    if (submitting || timesheetData?.status === "submitted") return;
    // A week that has not started yet has nothing to report.
    if (isFutureWeek) return;
    // Nor does a week that predates every project — there was nothing to
    // work on, so an empty timesheet must not be submittable for it.
    if (isBeforeAnyProject) return;
    setSubmitting(true);
    try {
      let res, json;
      if (!timesheetData?._id) {
        // Create then submit
        const allEntries = (timesheetData?.projects || []).flatMap((p) =>
          (p.entries || []).map((e) => ({
            project: e.project?._id || e.project,
            task: e.task?._id || e.task,
            logs: (e.logs || []).map((l) => ({
              date: l.dateKey || toDateKey(l.date),
              hours: Number(l.hours) || 0,
              isLeave: l.isLeave || false,
            })),
          })),
        );
        res = await authFetch("/api/timesheets", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            startDate: startDateStr,
            endDate: toDateKey(new Date(monday.getTime() + 6 * 86400000)),
            entries: allEntries,
          }),
        });
        json = await readJson(res);
        if (!res.ok || !json?.data?._id) {
          setError(json?.message || "Failed to save.");
          return;
        }
        setTimesheetData(json.data);
        // Now submit
        res = await authFetch(`/api/timesheets/${json.data._id}/submit`, {
          method: "PATCH",
        });
        json = await readJson(res);
      } else {
        res = await authFetch(`/api/timesheets/${timesheetData._id}/submit`, {
          method: "PATCH",
        });
        json = await readJson(res);
      }
      if (res.ok && json?.data) {
        setTimesheetData(json.data);
        setSuccessMsg(
          isAuto
            ? "Timesheet auto-submitted."
            : "Timesheet submitted successfully!",
        );
        setTimeout(() => setSuccessMsg(""), 3000);
      } else {
        setError(json?.message || "Submission failed.");
        setTimeout(() => setError(""), 3000);
      }
    } catch (err) {
      setError(describeFetchError(err));
      setTimeout(() => setError(""), 3000);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Derived ──────────────────────────────────────────────────────────────
  const isSubmitted = timesheetData?.status === "submitted";
  // Cells are read-only once the week is submitted, and for any week that has
  // not started yet.
  const isLocked = isSubmitted || isFutureWeek || isBeforeAnyProject;
  const weekDays = timesheetData?.weekDays || [];
  const projects = timesheetData?.projects || [];
  const hasProjects = projects.length > 0;

  // Today, in the same YYYY-MM-DD shape the server sends for each day, so
  // the current column can be highlighted. Built from local date parts —
  // toISOString() would roll over to the next day for anyone east of UTC.
  const todayKey = (() => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  })();

  // Per-day and whole-week totals across every project, for the footer row.
  // Derived here rather than asking the server, since the numbers it would
  // add up are already on the page.
  const weekTotals = weekDays.map((day) =>
    projects.reduce((sum, proj) => {
      const perProject = (proj.entries || []).reduce((n, entry) => {
        const log = (entry.logs || []).find((l) => l.dateKey === day.dateKey);
        return n + (Number(log?.hours) || 0);
      }, 0);
      return sum + perProject;
    }, 0),
  );
  const weekGrandTotal = weekTotals.reduce((a, b) => a + b, 0);

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="p-2 sm:p-4 md:p-6">
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {/* Header */}
        <div className="px-4 sm:px-6 pt-5 sm:pt-8 pb-5 sm:pb-8 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                Timesheet
              </h2>
              <p className="text-xs sm:text-sm text-gray-400 mt-0.5">
                Track And Manage Your Work Hours Seamlessly
              </p>
            </div>

            {/* Date range dropdown — pick any date, snaps to that week (Mon–Sun) */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowRangePicker((prev) => !prev)}
                className="flex items-center gap-2 h-9 px-3 border border-gray-200 rounded-lg text-xs sm:text-sm text-gray-700 hover:bg-gray-50 transition"
              >
                <Calendar size={14} className="text-gray-400" />
                {formatRangeLabel(monday, weekEnd)}
                <ChevronDown
                  size={14}
                  className={`text-gray-400 transition-transform ${
                    showRangePicker ? "rotate-180" : ""
                  }`}
                />
              </button>

              {showRangePicker && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowRangePicker(false)}
                  />
                  <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg">
                    <DatePicker
                      inline
                      selected={anchorDate}
                      onChange={(date) => {
                        setAnchorDate(date);
                        setShowRangePicker(false);
                      }}
                      calendarStartDay={1}
                      // Days before the earliest project began are greyed out
                      // and unselectable — there is nothing to log then.
                      minDate={minSelectableDate || undefined}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Future weeks are read-only — say so, so the disabled cells do not
            look like a bug. */}
        {isFutureWeek && !loading && (
          <div className="mx-4 mb-3 rounded-lg border border-[#D4A72C] bg-[#FFF8E5] px-4 py-3">
            <p className="text-xs text-[#7D4E00]">
              This week hasn't started yet. Hours can only be logged for the
              current week.
            </p>
          </div>
        )}

        {/* Week predates every project, so there is nothing to log or submit. */}
        {isBeforeAnyProject && !loading && (
          <div className="mx-4 mb-3 rounded-lg border border-[#D4A72C] bg-[#FFF8E5] px-4 py-3">
            <p className="text-xs text-[#7D4E00]">
              No projects existed during this week
              {minSelectableDate
                ? ` — the earliest project starts ${earliestProjectStart.toLocaleDateString(
                    "en-GB",
                    { day: "2-digit", month: "short", year: "numeric" },
                  )}.`
                : "."}
            </p>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400 text-sm">
            Loading timesheet...
          </div>
        ) : !hasProjects ? (
          <div className="flex items-center justify-center py-20 text-gray-400 text-sm">
            No tasks assigned to you yet.
          </div>
        ) : (
          <div className="overflow-x-auto px-4 sm:px-6 pb-6">
            <table className="w-full text-sm border-collapse rounded-[10px] overflow-hidden ring-1 ring-[#E8EAF2]">
              <thead>
                <tr className="bg-[#4F46E5] border-b border-[#4338CA]">
                  <th className="text-left py-3 px-5 text-white font-medium text-[13px] border-r border-white/20">
                    Project &amp; Task
                  </th>
                  {weekDays.map((d, i) => {
                    const { day, weekday } = formatHeaderDate(d.date);
                    // Today's column is tinted across the whole table, so the
                    // day being logged is obvious at a glance.
                    const today = d.dateKey === todayKey;
                    return (
                      <th
                        key={i}
                        className={`px-3 py-3 text-center ${today ? "bg-[#4338CA]" : ""}`}
                      >
                        <div className="flex flex-col items-center leading-tight">
                          <span
                            className="text-[10px] font-semibold uppercase tracking-[0.04em] text-white/80"
                          >
                            {day.split(" ")[0]} {day.split(" ")[1]}
                          </span>
                          <span
                            className="text-[13px] font-semibold flex items-center gap-1 text-white"
                          >
                            {weekday}
                            {today && (
                              <span
                                className="h-1.5 w-1.5 rounded-full bg-white"
                                aria-label="Today"
                              />
                            )}
                          </span>
                        </div>
                      </th>
                    );
                  })}
                  <th className="px-4 py-3 text-center text-white font-medium text-[13px] border-l border-white/20">
                    Comments
                  </th>
                </tr>
              </thead>
              <tbody>
                {projects.map((proj, pIdx) => (
                  <React.Fragment key={proj.project._id || pIdx}>
                    {/* Project name row */}
                    <tr className="bg-[#FAFBFD] border-y border-[#E8EAF2]">
                      <td className="px-5 py-2.5 border-r border-[#E8EAF2]">
                        <span className="flex items-center gap-2">
                          <span className="shrink-0 flex h-6 w-6 items-center justify-center rounded-[6px] bg-[#EEF0FF] text-[#4F46E5]">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5" aria-hidden="true"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" /></svg>
                          </span>
                          <span className="text-[13px] font-semibold text-[#1F2937]">
                            {proj.project.name}
                          </span>
                        </span>
                      </td>
                      {weekDays.map((d, i) => (
                        <td
                          key={i}
                          className={d.dateKey === todayKey ? "bg-[#F5F6FF]" : ""}
                        />
                      ))}
                      <td className="border-l border-[#E8EAF2] px-4 text-right">
                        <span className="inline-block rounded-full bg-[#EDEFF5] px-2 py-[2px] text-[10px] font-medium text-[#4F46E5] whitespace-nowrap">
                          {(proj.entries || []).length} task
                          {(proj.entries || []).length === 1 ? "" : "s"}
                        </span>
                      </td>
                    </tr>

                    {/* Task rows */}
                    {(proj.entries || []).map((entry, eIdx) => (
                      <tr
                        key={entry._id || eIdx}
                        className="border-b border-[#F1F3F8] last:border-b-0 hover:bg-[#FCFCFE] transition-colors"
                      >
                        <td className="px-5 py-2 text-[#4B5563] text-[12px] border-r border-[#E8EAF2]">
                          {/* A dot marks a task with hours logged this week,
                              matching the design's leading bullet. */}
                          <span className="flex items-center gap-2">
                            {(entry.logs || []).some(
                              (l) => Number(l.hours) > 0,
                            ) ? (
                              <span className="shrink-0 h-1.5 w-1.5 rounded-full bg-[#4F46E5]" />
                            ) : (
                              <span className="shrink-0 h-1.5 w-1.5" />
                            )}
                            <span className="truncate" title={entry.task?.name}>
                              {entry.task?.name}
                            </span>
                          </span>
                        </td>
                        {weekDays.map((day, dIdx) => {
                          const log =
                            (entry.logs || []).find(
                              (l) => l.dateKey === day.dateKey,
                            ) || {};
                          const cellKey = `${pIdx}-${eIdx}-${day.dateKey}`;
                          const isEdit = editingCell === cellKey;
                          const err = cellError[cellKey];
                          // Day falls before this project started, so it is
                          // read-only even in an otherwise editable week.
                          const beforeStart = isBeforeProjectStart(
                            day.dateKey,
                            proj.project?.startDate,
                          );
                          const cellLocked = isLocked || beforeStart;

                          if (log.isLeave) {
                            return (
                              <td
                                key={dIdx}
                                className={`px-3 py-2 text-center ${day.dateKey === todayKey ? "bg-[#F5F6FF]" : ""}`}
                              >
                                <span className="text-[11px] font-semibold text-[#B54708] bg-[#FFFAEB] px-2 py-1 rounded-[6px]">
                                  Leave
                                </span>
                              </td>
                            );
                          }

                          return (
                            <td
                              key={dIdx}
                              className={`px-3 py-2 text-center relative ${day.dateKey === todayKey ? "bg-[#F5F6FF]" : ""}`}
                            >
                              {isEdit && !cellLocked ? (
                                <input
                                  ref={focusEditInput}
                                  className="w-14 text-center border border-[#4F46E5] rounded-[6px] text-xs py-1 outline-none"
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  onBlur={() =>
                                    handleCellBlur(
                                      entry._id,
                                      day.dateKey,
                                      editValue,
                                      cellKey,
                                      pIdx,
                                      eIdx,
                                    )
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") e.target.blur();
                                    if (e.key === "Escape")
                                      setEditingCell(null);
                                  }}
                                />
                              ) : (
                                <button
                                  disabled={cellLocked}
                                  title={
                                    beforeStart
                                      ? "This project had not started on this date."
                                      : isFutureWeek
                                        ? "Hours can only be logged for the current week."
                                        : undefined
                                  }
                                  onClick={() => {
                                    if (cellLocked) return;
                                    setEditingCell(cellKey);
                                    setEditValue(
                                      Number(log.hours) > 0
                                        ? String(log.hours)
                                        : "",
                                    );
                                  }}
                                  className={`rounded-[6px] px-2 py-1 text-xs min-w-[46px] transition-colors ${
                                    Number(log.hours) > 0
                                      ? "bg-[#EEF0FF] text-[#4F46E5] font-semibold"
                                      : beforeStart
                                        ? "text-[#D1D5DB]"
                                        : "text-[#C3C8D4]"
                                  } ${cellLocked ? "cursor-not-allowed" : "hover:bg-[#F1F2F9] cursor-pointer"}`}
                                >
                                  {Number(log.hours) > 0
                                    ? `${Number(log.hours).toFixed(2)}`
                                    : "–"}
                                </button>
                              )}
                              {err && (
                                <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 z-10 bg-orange-50 border border-orange-200 text-orange-600 text-xs px-2 py-0.5 rounded whitespace-nowrap">
                                  {err}
                                </div>
                              )}
                            </td>
                          );
                        })}
                        <td className="px-4 py-2 border-l border-[#E8EAF2] text-center">
                          {/* An entry that already has comments is filled, so
                              it stands out from the plain "Comment" prompt. */}
                          <button
                            onClick={() => handleOpenComments(entry)}
                            className={`inline-flex items-center gap-1.5 rounded-[6px] px-2.5 py-1 text-[11px] transition-colors ${
                              entry.commentCount > 0
                                ? "bg-[#EEF0FF] text-[#4F46E5] font-medium"
                                : "text-[#9CA3AF] hover:bg-[#F4F5FA] hover:text-[#4F46E5]"
                            }`}
                          >
                            <MessageSquare size={13} />
                            {entry.commentCount > 0
                              ? `${entry.commentCount} Comments`
                              : "Comment"}
                          </button>
                        </td>
                      </tr>
                    ))}

                    {/* Per-project daily totals. Kept because a timesheet
                        with several projects needs each one's subtotal, not
                        just the week's grand total below. */}
                    <tr className="bg-[#FCFCFE] border-t border-[#E8EAF2]">
                      <td className="px-5 py-2 text-[12px] font-medium text-[#4F46E5] border-r border-[#E8EAF2]">
                        Subtotal
                      </td>
                      {(proj.dailyTotals || []).map((t, i) => (
                        <td
                          key={i}
                          className={`px-3 py-2 text-center text-[12px] font-medium text-[#4B5563] ${weekDays[i]?.dateKey === todayKey ? "bg-[#F5F6FF]" : ""}`}
                        >
                          {t.display === "Leave" ? (
                            <span className="text-amber-500 font-semibold">
                              Leave
                            </span>
                          ) : (
                            t.display
                          )}
                        </td>
                      ))}
                      <td className="border-l border-[#E8EAF2]" />
                    </tr>
                  </React.Fragment>
                ))}

                {/* TOTAL HOURS — the whole week, across every project. */}
                <tr className="bg-white border-t-2 border-[#E8EAF2]">
                  <td className="px-5 py-3 text-[13px] font-bold uppercase tracking-[0.04em] text-[#4F46E5] border-r border-[#E8EAF2]">
                    Total Hours
                  </td>
                  {weekTotals.map((total, i) => (
                    <td
                      key={i}
                      className={`px-3 py-3 text-center text-[13px] font-bold ${
                        weekDays[i]?.dateKey === todayKey
                          ? "bg-[#F5F6FF] text-[#4F46E5]"
                          : total > 0
                            ? "text-[#1F2937]"
                            : "text-[#9CA3AF]"
                      }`}
                    >
                      {total.toFixed(2)}
                    </td>
                  ))}
                  <td className="px-4 py-3 border-l border-[#E8EAF2] text-center">
                    <span className="inline-block rounded-[6px] bg-[#EEF0FF] px-2.5 py-1 text-[12px] font-bold text-[#4F46E5] whitespace-nowrap">
                      {weekGrandTotal.toFixed(2)} hrs
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Notice cards */}
      {hasProjects && !loading && (
        <div className="flex flex-col sm:flex-row gap-4 mt-4">
          <div className="flex-1 h-[83px] bg-white rounded flex items-center justify-center gap-3">
            <Icons.Triangle />

            <span
              className="text-sm text-black/50 capitalize"
              style={{ fontFamily: "Poppins, sans-serif" }}
            >
              You can update your entries anytime. Deleting entries is
              restricted.
            </span>
          </div>

          <div className="flex-1 h-[83px] bg-white rounded flex items-center justify-center gap-3">
            <Icons.Sheild />
            <span
              className="text-sm text-black/[.53] capitalize"
              style={{ fontFamily: "Poppins, sans-serif" }}
            >
              Once submitted, cannot be retrieved
            </span>
          </div>
        </div>
      )}

      {/* Submit row */}
      {hasProjects && !loading && (
        <div className="flex items-center justify-end gap-4 mt-4">
          {saving && <span className="text-xs text-gray-400">Saving...</span>}
          {error && (
            <span className="text-xs text-red-500 font-medium">{error}</span>
          )}
          {successMsg && (
            <span className="text-xs text-green-600 font-semibold">
              ✓ {successMsg}
            </span>
          )}
          {isSubmitted ? (
            <span className="px-5 py-2 bg-gray-100 text-gray-400 rounded-lg text-sm font-semibold cursor-not-allowed">
              Already Submitted
            </span>
          ) : isFutureWeek ? (
            <span className="px-5 py-2 bg-gray-100 text-gray-400 rounded-lg text-sm font-semibold cursor-not-allowed">
              Not Available Yet
            </span>
          ) : isBeforeAnyProject ? (
            <span className="px-5 py-2 bg-gray-100 text-gray-400 rounded-lg text-sm font-semibold cursor-not-allowed">
              No Projects Yet
            </span>
          ) : null /* No manual Submit: timesheets are auto-submitted at the
                      end of the week (backend scheduler, plus the fallback
                      timer above that calls handleSubmit(true)). */}
        </div>
      )}

      {/* Comment Modal */}
      {commentModal && (
        <CommentModal
          taskId={commentModal.taskId}
          taskName={commentModal.taskName}
          commentCount={commentModal.commentCount}
          isSubmitted={isLocked}
          onClose={() => setCommentModal(null)}
        />
      )}
    </div>
  );
};

export default Timesheet;
